import { TextLink } from "@abb/arcadia-ui-react"
import { useCallback, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useDeleteConfirm } from "../../../../../../../hooks/useDeleteConfirm"
import {
  ApplicationRoleApproverGet,
  ApplicationRoleApproverProperty,
} from "../../../../../../models/ApplicationRoleApprover"
import { DimaFeatureShortNameEnum } from "../../../../../../models/Authorization"
import { deleteEntity } from "../../../../../../utils/deleteEntity"
import { booleanComparator, getDoneIcon, getEmptyIcon } from "../../../../../../utils/getBooleanFlagIcon"
import { DiMaAddButton } from "../../../../../partials/DiMaAddButton"
import { EntityRow, EntityTab } from "../../../../../partials/EntityTab"

export function ApplicationRoleApproverProperties({
  approver,
}: {
  readonly approver: ApplicationRoleApproverGet
}) {
  const { applicationId, roleId, userId } = useParams()
  const navigate = useNavigate()
  const { requestDelete, DeleteConfirmDialog } = useDeleteConfirm()

  const handleNavigate = useCallback(
    (propertyName: string, value: string) => {
      navigate(
        `/applications/${applicationId}/roles/${roleId}/approvers/${userId}/properties/${propertyName}/values/${value}`
      )
    },
    [navigate, applicationId, roleId, userId]
  )

  const handlePropertyClick = useCallback(
    (propertyName: string, value: string) => () => handleNavigate(propertyName, value),
    [handleNavigate]
  )

  const columns = [
    { field: "propertyName", headerName: "Property name" },
    { field: "propertyLabel", headerName: "Property label" },
    { field: "approverType", headerName: "Approver Type" },
    {
      field: "isAutomatic",
      headerName: "Is Automatic",
      renderCell: (row: EntityRow) => row.isAutomatic ? getDoneIcon() : getEmptyIcon(),
      sortComparator: booleanComparator,
    },
  ]

  const rows = approver.properties.map(
    (property: ApplicationRoleApproverProperty, index: number) => ({
      id: index.toString(),
      propertyName: (
        <TextLink
          text={property.name ?? ""}
          size={14}
          onClick={handlePropertyClick(property.name ?? "", property.value ?? "")}
        />
      ),
      propertyLabel: property.label ?? "",
      approverType: property.approverType ?? "",
      isAutomatic: property.isAutomatic,
      propertyValue: property.value ?? "",
    })
  )

  const toolbar = useMemo(
    () => (
      <DiMaAddButton
        url={`/applications/${applicationId}/roles/${roleId}/approvers/${userId}/create`}
        feature={DimaFeatureShortNameEnum.AM}
      />
    ),
    [applicationId, roleId, userId]
  )

  const handleActionMenuClick = useCallback((item: { title: string }, row: EntityRow) => {
      if (item.title === "Delete") {
        requestDelete(() =>
          deleteEntity(
            `/applications/${applicationId}/roles/${roleId}/approvers/${userId}/properties/${row.propertyName}/values/${row.propertyValue}`
          )
        )
      }
    }, [applicationId, roleId, userId, requestDelete])

  return (
    <>
      {DeleteConfirmDialog}
      <EntityTab
        rows={rows}
        cols={columns}
        isLoading={false}
        toolbar={toolbar}
        actionItems={[{ title: "Delete", iconName: "icon_abb_dias icon-abb_delete" }]}
        onActionMenuClick={handleActionMenuClick}
      />
    </>
  )
}
